import React, { useState, useMemo, useCallback } from "react";
import { Card, Button, Input, Table, Space, Tag, Badge, Modal, message, Dropdown, Typography, Tooltip } from "antd";
import { useMutation } from "react-query";
import { useTableSelection, useLoadList, useQuickSearch } from "../../../../hooks";
import { scenarioContextEnhancedService } from "../../../../api/enhancedApi";
import { formatDateTime } from "../../../../utils/dateUtils";
import { getStandardCostTypeLabel } from "../../../settlement/constants";
import ScenarioContextFilter from "./ScenarioContextFilter";
import ScenarioContextForm from "./ScenarioContextForm";
import ProductListViewer from "./ProductListViewer";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, DownOutlined, EyeOutlined, DatabaseOutlined, FileTextOutlined, BookOutlined } from "@ant-design/icons";
import { useAuth } from "../../../../hooks/useAuth";
import PermissionGuard from "../../../../components/auth/PermissionGuard";
import CollapsibleFilterBar from "../../../../components/common/CollapsibleFilterBar";
import { getScenarioContextAgentLabel } from "../constants";

const { confirm } = Modal;
const { Text, Paragraph } = Typography;

const SCENARIO_CONDITION_DISPLAY_KEYS = new Set(["supplier_name", "supplier_code", "supplier_display_name"]);
const HANDLED_SCENARIO_CONDITION_KEYS = new Set(["supplier_id", "standard_cost_type", "knowledge_domain", "strategy_label", ...SCENARIO_CONDITION_DISPLAY_KEYS]);

const getSupplierConditionDisplay = (conditions) => {
    if (!conditions) return "";
    if (conditions.supplier_display_name) return conditions.supplier_display_name;
    if (conditions.supplier_code && conditions.supplier_name) return `${conditions.supplier_code} - ${conditions.supplier_name}`;
    return conditions.supplier_name || conditions.supplier_id;
};

/**
 * 场景化上下文管理表格组件（统一实现）
 *
 * 同时服务于：
 * 1. 独立页面（ScenarioContextList）：embedded=false，显示完整页面容器
 * 2. 嵌入业务维护壳或其他模态框：embedded=true，自动合并预定义筛选条件和初始表单值
 *
 * @param {boolean} embedded - 是否为嵌入模式
 * @param {object} predefinedFilters - 预定义 API 查询过滤条件（如 { scenario_supplier_id: 123 }）
 * @param {object} predefinedFilterLabels - 预定义筛选条件的可读标签（如 { scenario_supplier_id: '某某供应商' }），用于摘要栏展示
 * @param {object} initialFormValues - 新建规则时的初始表单值（如 { scenario_conditions: { supplier_id: 123 } }）
 */
const ScenarioContextTable = ({ embedded = false, predefinedFilters = {}, predefinedFilterLabels = {}, initialFormValues = {} }) => {
    // 获取权限相关功能
    const { hasPermission } = useAuth();

    // 状态定义
    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });
    const [formVisible, setFormVisible] = useState(false);
    const [currentPrompt, setCurrentPrompt] = useState(null);
    const [formMode, setFormMode] = useState("add");
    const [filterCollapsed, setFilterCollapsed] = useState(
        // 如果 predefinedFilters 中有高级筛选字段，初始不折叠
        false
    );
    const [productViewerVisible, setProductViewerVisible] = useState(false);
    const [selectedProductRecord, setSelectedProductRecord] = useState(null);


    // 构造API请求参数
    const apiParams = useMemo(() => {
        const params = {
            page: pagination.current,
            page_size: pagination.pageSize,
            ...filterParams,
            ...predefinedFilters, // 合并预定义过滤条件
        };
        return params;
    }, [pagination.current, pagination.pageSize, filterParams, predefinedFilters]);

    // 使用自定义Hook加载数据
    const { data: promptsData, isLoading, refetch } = useLoadList("场景化提示词", scenarioContextEnhancedService.getList, apiParams);

    // 删除场景化提示词
    const deleteMutation = useMutation({
        mutationFn: scenarioContextEnhancedService.delete,
        onSuccess: (response) => {
            if (response.data.success) {
                message.success(response.data.message || "删除成功");
                refetch();
            } else {
                message.error(response.data.message || "删除失败");
            }
        },
        onError: (error) => {
            message.error(error.response?.data?.detail || "删除失败");
        },
    });

    // 使用自定义Hook处理表格选择
    const { selectedRowKeys, tableRef, rowSelection, getIndexColumn, getRowProps, clearSelection } = useTableSelection({
        getRowKey: (record) => record.id,
        dataSource: promptsData?.items || [],
        pagination,
    });

    // 知识领域标签映射
    const KNOWLEDGE_DOMAIN_LABELS = {
        ap_products: "产品知识库",
        ap_billing_rules: "计费规则",
    };

    const KNOWLEDGE_DOMAIN_COLORS = {
        ap_products: "green",
        ap_billing_rules: "purple",
    };

    // 渲染场景条件（语义化展示）
    const renderScenarioConditions = (conditions) => {
        if (!conditions || Object.keys(conditions).length === 0) {
            return <Text type="secondary">-</Text>;
        }

        const tags = [];

        // 供应商
        if (conditions.supplier_id) {
            const supplierDisplay = getSupplierConditionDisplay(conditions);
            tags.push(
                <Tag key="supplier" color="blue">
                    供应商: {supplierDisplay}
                </Tag>
            );
        }

        // 费用类型
        if (conditions.standard_cost_type) {
            tags.push(
                <Tag key="cost_type" color="orange">
                    {getStandardCostTypeLabel(conditions.standard_cost_type)}
                </Tag>
            );
        }

        // 知识领域
        if (conditions.knowledge_domain) {
            const domainLabel = KNOWLEDGE_DOMAIN_LABELS[conditions.knowledge_domain] || conditions.knowledge_domain;
            const domainColor = KNOWLEDGE_DOMAIN_COLORS[conditions.knowledge_domain] || "default";
            tags.push(
                <Tag key="domain" color={domainColor} icon={<BookOutlined />}>
                    {domainLabel}
                </Tag>
            );
        }

        if (conditions.strategy_label) {
            tags.push(
                <Tag key="strategy_label" color="purple">
                    策略: {conditions.strategy_label}
                </Tag>
            );
        }

        // 其他未识别的条件（向后兼容）
        Object.entries(conditions).forEach(([key, value]) => {
            if (!HANDLED_SCENARIO_CONDITION_KEYS.has(key)) {
                tags.push(
                    <Tag key={key} color="default">
                        {key}: {value}
                    </Tag>
                );
            }
        });

        return <Space wrap>{tags}</Space>;
    };

    // 定义表格列
    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "Agent名称",
            dataIndex: "agent_name",
            key: "agent_name",
            width: 180,
            render: (text) => <Text strong>{getScenarioContextAgentLabel(text)}</Text>,
        },
        {
            title: "类型",
            dataIndex: "entry_type",
            key: "entry_type",
            width: 100,
            render: (entryType) => {
                const typeConfig = {
                    PROMPT: { color: "blue", icon: <FileTextOutlined />, label: "提示词" },
                    KNOWLEDGE: { color: "green", icon: <DatabaseOutlined />, label: "知识库" },
                    CONTEXT: { color: "orange", icon: <FileTextOutlined />, label: "上下文" },
                };
                const config = typeConfig[entryType] || { color: "default", label: entryType };
                return (
                    <Tag color={config.color} icon={config.icon}>
                        {config.label}
                    </Tag>
                );
            },
        },
        {
            title: "场景条件",
            dataIndex: "scenario_conditions",
            key: "scenario_conditions",
            width: 250,
            render: renderScenarioConditions,
        },
        {
            title: "内容",
            key: "content",
            width: 220,
            ellipsis: true,
            render: (_, record) => {
                // KNOWLEDGE 类型：根据 knowledge_domain 区分展示
                if (record.entry_type === "KNOWLEDGE") {
                    const domain = record.scenario_conditions?.knowledge_domain;

                    if (domain === "ap_billing_rules") {
                        // 计费规则：显示文本预览
                        const rulesText = record.json_content?.rules_text || "";
                        const previewLen = rulesText.length;
                        return (
                            <Space>
                                <Tag color="purple">规则 {previewLen} 字</Tag>
                                <Tooltip title={rulesText.substring(0, 200) + (previewLen > 200 ? "..." : "")}>
                                    <Button type="link" size="small" icon={<EyeOutlined />}>
                                        预览
                                    </Button>
                                </Tooltip>
                            </Space>
                        );
                    }

                    // 产品知识库：显示数量 + 查看按钮
                    const products = record.json_content?.products || [];
                    const productCount = products.length;
                    const unitCount = products.filter((product) => product.unit).length;
                    return (
                        <Space>
                            <Tag color="green">{productCount} 个产品</Tag>
                            {unitCount > 0 && <Tag color="geekblue">{unitCount} 已配单位</Tag>}
                            <Tooltip title="查看产品列表">
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<EyeOutlined />}
                                    onClick={() => {
                                        setSelectedProductRecord(record);
                                        setProductViewerVisible(true);
                                    }}
                                >
                                    查看
                                </Button>
                            </Tooltip>
                        </Space>
                    );
                }
                // PROMPT 或 CONTEXT 类型显示文本内容
                return (
                    <Paragraph ellipsis={{ rows: 2, expandable: true }} style={{ margin: 0 }}>
                        {record.prompt_text || <Text type="secondary">-</Text>}
                    </Paragraph>
                );
            },
        },
        {
            title: "状态",
            dataIndex: "is_active",
            key: "is_active",
            width: 100,
            render: (is_active) => <Badge className="status-badge" status={is_active ? "success" : "error"} text={is_active ? "启用" : "停用"} />,
        },
        {
            title: "创建时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 160,
            render: (text) => formatDateTime(text),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 150,
            render: (_, record) => (
                <Space size="small" className="table-actions">
                    <PermissionGuard permission="agent:update">
                        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                            编辑
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="agent:delete">
                        <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
                            删除
                        </Button>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    // 处理表格分页变化
    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    // 处理搜索
    const handleSearch = (values) => {
        const params = {};

        // 处理基础筛选条件
        if (values.agent_name) {
            params.agent_name = values.agent_name;
        }
        if (values.entry_type) {
            params.entry_type = values.entry_type;
        }
        if (values.is_active !== undefined && values.is_active !== null) {
            params.is_active = values.is_active;
        }
        if (values.description) {
            params.description = values.description;
        }

        // 处理高级筛选条件
        if (values.created_at_start) {
            params.created_at_start = values.created_at_start;
        }
        if (values.created_at_end) {
            params.created_at_end = values.created_at_end;
        }
        if (values.scenario_supplier_id) {
            params.scenario_supplier_id = values.scenario_supplier_id;
        }
        if (values.scenario_standard_cost_type) {
            params.scenario_standard_cost_type = values.scenario_standard_cost_type;
        }
        if (values.scenario_knowledge_domain) {
            params.scenario_knowledge_domain = values.scenario_knowledge_domain;
        }
        if (values.prompt_text) {
            params.prompt_text = values.prompt_text;
        }

        // 处理排序
        if (values.sort_field) {
            params.sort_field = values.sort_field;
            params.sort_order = values.sort_order || "desc";
        }

        setFilterParams(params);
        setPagination((prev) => ({
            ...prev,
            current: 1,
        }));
    };

    // 快捷搜索
    const quickSearch = useQuickSearch({ setFilterParams, setPagination });

    // 重置搜索：清空用户手动设置的筛选，但保留 predefinedFilters 对应的 API 参数
    const handleReset = () => {
        setFilterParams({});
        setPagination((prev) => ({
            ...prev,
            current: 1,
        }));
    };

    // 当 Filter 检测到预设条件涉及高级筛选字段时，自动展开高级筛选区
    const handlePredefinedInAdvanced = useCallback(() => {
        setFilterCollapsed(false);
    }, []);

    // 切换筛选器折叠状态
    const handleToggleFilterCollapse = () => {
        setFilterCollapsed((prev) => !prev);
    };

    // 筛选摘要项：合并 filterParams（用户手动筛选）和 predefinedFilters（外部预设）
    const filterSummaryItems = React.useMemo(() => {
        const items = [];
        // 合并两个来源的有效筛选参数
        const merged = { ...predefinedFilters, ...filterParams };

        if (merged.agent_name) items.push({ label: "Agent", value: getScenarioContextAgentLabel(merged.agent_name) });
        if (merged.entry_type) {
            const typeMap = { PROMPT: "提示词", KNOWLEDGE: "知识库", CONTEXT: "上下文" };
            items.push({ label: "类型", value: typeMap[merged.entry_type] || merged.entry_type });
        }
        if (merged.is_active !== undefined) {
            items.push({ label: "状态", value: merged.is_active ? "启用" : "停用" });
        }
        if (merged.description) items.push({ label: "描述", value: merged.description });
        if (merged.scenario_supplier_id) {
            const isPredefined = predefinedFilters.scenario_supplier_id !== undefined;
            // 优先使用 predefinedFilterLabels 中的可读名称，回退到 ID
            const displayName = predefinedFilterLabels.scenario_supplier_id || merged.scenario_supplier_id;
            items.push({
                label: "供应商",
                value: isPredefined ? `${displayName} (预设)` : String(displayName),
            });
        }
        if (merged.scenario_standard_cost_type) items.push({ label: "费用类型", value: merged.scenario_standard_cost_type });
        return items;
    }, [filterParams, predefinedFilters, predefinedFilterLabels]);

    // 添加场景化提示词
    const handleAdd = () => {
        setFormMode("add");
        // 合并初始值（嵌入模式下自动带入供应商等预设值）
        setCurrentPrompt(Object.keys(initialFormValues).length > 0 ? initialFormValues : null);
        setFormVisible(true);
    };

    // 编辑场景化提示词
    const handleEdit = (record) => {
        if (!hasPermission("agent:update")) {
            message.error("您没有编辑场景化提示词的权限");
            return;
        }
        setFormMode("edit");
        setCurrentPrompt(record);
        setFormVisible(true);
    };

    // 删除场景化提示词
    const handleDelete = (record) => {
        if (!hasPermission("agent:delete")) {
            message.error("您没有删除场景化提示词的权限");
            return;
        }

        if (!record || !record.id) {
            message.error("无效的记录数据");
            return;
        }

        confirm({
            title: "确认删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除"${getScenarioContextAgentLabel(record.agent_name)}"的场景化提示词吗？此操作不可恢复。`,
            okText: "确认",
            okType: "danger",
            cancelText: "取消",
            onOk() {
                deleteMutation.mutate(record.id);
            },
        });
    };

    // 批量删除
    const handleBatchDelete = () => {
        if (!hasPermission("agent:delete")) {
            message.error("您没有删除场景化提示词的权限");
            return;
        }

        if (!selectedRowKeys || selectedRowKeys.length === 0) {
            message.warning("请先选择要删除的项目");
            return;
        }

        confirm({
            title: "确认批量删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除选中的 ${selectedRowKeys.length} 个场景化提示词吗？此操作不可恢复。`,
            okText: "确认",
            okType: "danger",
            cancelText: "取消",
            onOk() {
                Promise.all(selectedRowKeys.map((id) => scenarioContextEnhancedService.delete(id)))
                    .then((responses) => {
                        const successCount = responses.filter((res) => res.data.success).length;
                        const failureCount = responses.length - successCount;

                        if (successCount > 0) {
                            message.success(`成功删除 ${successCount} 个场景化提示词`);
                            clearSelection();
                            refetch();
                        }

                        if (failureCount > 0) {
                            console.error(`有 ${failureCount} 个删除操作失败`);
                        }
                    })
                    .catch((error) => {
                        message.error(`批量删除失败: ${error.message}`);
                    });
            },
        });
    };

    // 批量操作菜单项
    const batchActionItems = [
        {
            key: "delete",
            label: "批量删除",
            disabled: selectedRowKeys.length === 0 || !hasPermission("agent:delete"),
            onClick: handleBatchDelete,
        },
    ];

    // 表单提交成功
    const handleFormSuccess = () => {
        setFormVisible(false);
        refetch();
    };

    // 工具栏按钮（仅在 embedded 模式下使用）
    const renderEmbeddedToolbar = () => (
        <div style={{ marginBottom: 8, display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <PermissionGuard permission="agent:create">
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAdd}>
                    添加提示词
                </Button>
            </PermissionGuard>
        </div>
    );

    return (
        <div>
            {/* 页面标题（仅独立页面模式） */}
            {!embedded && (
                <div className="page-header-compact">
                    <div className="page-title-compact">场景化上下文管理</div>
                    <div className="page-action-compact">
                        <Space>
                            <PermissionGuard permission="agent:create">
                                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                                    添加提示词
                                </Button>
                            </PermissionGuard>
                            <PermissionGuard permission="agent:delete">
                                <Dropdown menu={{ items: batchActionItems }} disabled={selectedRowKeys.length === 0}>
                                    <Button>
                                        批量操作 <DownOutlined />
                                    </Button>
                                </Dropdown>
                            </PermissionGuard>
                        </Space>
                    </div>
                </div>
            )}

            {/* 嵌入模式操作栏 */}
            {embedded && renderEmbeddedToolbar()}

            {/* 筛选栏 */}
            <CollapsibleFilterBar
                summaryItems={filterSummaryItems}
                onReset={handleReset}
                drawerTitle="场景化上下文 - 筛选条件"
                quickControls={
                    <Input
                        placeholder="搜索名称/描述/内容/条件..."
                        style={{ width: 260, height: 26 }}
                        {...quickSearch.inputProps}
                    />
                }
            >
                <ScenarioContextFilter
                    onSearch={handleSearch}
                    onReset={handleReset}
                    collapsed={filterCollapsed}
                    onToggleCollapse={embedded ? handleToggleFilterCollapse : undefined}
                    autoApply
                    predefinedFilters={predefinedFilters}
                    onPredefinedInAdvanced={handlePredefinedInAdvanced}
                />
            </CollapsibleFilterBar>

            {/* 选中项统计 */}
            {selectedRowKeys.length > 0 && (
                <div className="batch-operations-info" style={{ marginBottom: 8, padding: "8px 0" }}>
                    已选择{" "}
                    <span className="selected-count" style={{ fontWeight: "bold", color: "#1890ff" }}>
                        {selectedRowKeys.length}
                    </span>{" "}
                    项
                    <Button type="link" onClick={clearSelection} size="small">
                        清空
                    </Button>
                </div>
            )}

            {/* 场景化提示词列表 */}
            <Card className={embedded ? "" : "data-card"} bordered={!embedded} bodyStyle={embedded ? { padding: 0 } : {}}>
                <Table
                    ref={tableRef}
                    size="small"
                    rowSelection={hasPermission("agent:delete") ? rowSelection : null}
                    columns={columns}
                    dataSource={promptsData?.items || []}
                    rowKey="id"
                    pagination={{
                        ...pagination,
                        total: promptsData?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
                        pageSizeOptions: ["10", "20", "50", "100"],
                    }}
                    loading={isLoading}
                    onChange={handleTableChange}
                    scroll={{ x: 1200 }}
                    onRow={getRowProps}
                />
            </Card>

            {/* 添加/编辑表单 */}
            {formVisible && <ScenarioContextForm visible={formVisible} mode={formMode} initialValues={currentPrompt} onCancel={() => setFormVisible(false)} onSuccess={handleFormSuccess} />}

            {/* 产品列表查看器 */}
            <ProductListViewer visible={productViewerVisible} onClose={() => setProductViewerVisible(false)} record={selectedProductRecord} />
        </div>
    );
};

export default ScenarioContextTable;
